<template>
    <student-backend-focus-layout
        content-bg-color="gray-125"
        exit-button-text="Save and Exit"
        v-on:exit-page-clicked="saveAndExit"
        :page-title="'Custom Deck'"
    >
        <template #page_header>
            <div class="flex items-center justify-between py-4">
                <content-editable
                    tag="div"
                    v-model="stackTitle"
                    :no-nl="true"
                    :no-html="true"
                    class="text-[26px] font-semibold"
                    @returned="saveStack"
                    @focusout="saveStack"
                />
                <div v-if="!isNewDeck">
                    <dropdown :px-width="140" open-from="right">
                        <template #button>
                            <div class="">
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
                                        stroke="#767C8D"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                    <path
                                        d="M12 6C12.5523 6 13 5.55228 13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5C11 5.55228 11.4477 6 12 6Z"
                                        stroke="#767C8D"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                    <path
                                        d="M12 20C12.5523 20 13 19.5523 13 19C13 18.4477 12.5523 18 12 18C11.4477 18 11 18.4477 11 19C11 19.5523 11.4477 20 12 20Z"
                                        stroke="#767C8D"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                            </div>
                        </template>
                        <template #content>
                            <div class="bg-white shadow">
                                <div
                                    class="whitespace-nowrap p-4 text-red-400 hover:cursor-pointer hover:text-red-500"
                                    @click="deleteStack"
                                >
                                    Delete Deck
                                </div>
                            </div>
                        </template>
                    </dropdown>
                </div>
            </div>
        </template>

        <template #content>
            <div class="w-full">
                <div
                    v-if="!flashcards.length"
                    class="flex w-full flex-col gap-4 bg-gray-300 px-12 py-4 text-center"
                >
                    <div class="font-semibold">No flashcards... yet</div>
                    <div class="text-[16px] text-gray-700">
                        This deck doesn't have any flashcards. Click on the “Add
                        a card” button to add new flashcards to this deck.
                    </div>
                    <div class="flex items-center justify-center">
                        <div
                            @click="addACard"
                            class="rounded bg-primary-525 px-2 py-2 text-white hover:cursor-pointer hover:bg-primary-600"
                        >
                            + Add a card
                        </div>
                    </div>
                </div>
                <div v-else class="">
                    <div class="flex flex-col">
                        <div
                            class="mb-4 mt-8 flex flex-row items-center justify-between gap-4"
                        >
                            <div class="font-semibold">All Cards</div>
                            <div
                                class="flex flex-grow items-center justify-between gap-4"
                                v-if="selectedFlashcardsCount"
                            >
                                <div
                                    @click="swapSelectedCards"
                                    class="flex items-center gap-2 rounded border border-gray-700 px-2 py-2 text-gray-700 hover:cursor-pointer hover:bg-gray-300"
                                >
                                    <div class="">
                                        <svg
                                            width="20"
                                            height="20"
                                            viewBox="0 0 20 20"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M6.25 17.5L2.5 13.75M2.5 13.75L6.25 10M2.5 13.75H13.75M13.75 2.5L17.5 6.25M17.5 6.25L13.75 10M17.5 6.25H6.25"
                                                stroke="currentColor"
                                                stroke-width="1.25"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            />
                                        </svg>
                                    </div>
                                    <div class="">
                                        Swap ({{ selectedFlashcardsCount }})
                                    </div>
                                </div>
                                <div
                                    @click="deleteSelectedCards"
                                    class="mr-auto rounded border border-red-400 px-2 py-2 text-red-400 hover:cursor-pointer hover:bg-red-400/10"
                                >
                                    Delete ({{ selectedFlashcardsCount }})
                                </div>
                                <div
                                    @click="selectAllCards"
                                    class="hover:primary-600 text-primary-525 hover:cursor-pointer"
                                >
                                    Select All
                                </div>
                                <div
                                    @click="clearSelectedCards"
                                    class="text-red-400 hover:cursor-pointer hover:text-red-500"
                                >
                                    Cancel
                                </div>
                            </div>
                            <div
                                @click="addACard"
                                class="rounded bg-primary-525 px-3 py-2 text-white hover:cursor-pointer hover:bg-primary-600"
                            >
                                + Add new card
                            </div>
                        </div>
                        <div
                            class="relative mb-4 flex w-full bg-white shadow"
                            v-for="(flashcard, index) in flashcards"
                        >
                            <div
                                class="relative min-h-[150px] w-1/2 break-words border border-r-0 border-gray-325 p-8 text-center text-gray-725"
                            >
                                <div
                                    class="absolute right-2 top-2 text-[12px] text-red-500"
                                    v-if="
                                        flashcard.front_content.length >=
                                        flashcardLengthLimit -
                                            showCharacterLimitWarningLimit
                                    "
                                >
                                    {{
                                        flashcardLengthLimit -
                                        flashcard.front_content.length
                                    }}
                                    chars remaining
                                </div>
                                <content-editable
                                    tag="div"
                                    v-model="flashcard.front_content"
                                    :no-nl="false"
                                    :no-html="true"
                                    class="min-h-[150px] outline-none"
                                    @focusout="throttledSaveStack"
                                    @keyup="
                                        trimFlashcardContent(
                                            index,
                                            'front_content',
                                            flashcard.front_content
                                        )
                                    "
                                />
                            </div>
                            <div
                                class="relative min-h-[150px] w-1/2 break-words border border-gray-325 p-8 text-center text-gray-725"
                            >
                                <div
                                    class="absolute right-2 top-2 text-[12px] text-red-500"
                                    v-if="
                                        flashcard.back_content.length >=
                                        flashcardLengthLimit -
                                            showCharacterLimitWarningLimit
                                    "
                                >
                                    {{
                                        flashcardLengthLimit -
                                        flashcard.back_content.length
                                    }}
                                    chars remaining
                                </div>
                                <content-editable
                                    tag="div"
                                    v-model="flashcard.back_content"
                                    :no-nl="false"
                                    :no-html="true"
                                    class="min-h-[150px] outline-none"
                                    @focusout="throttledSaveStack"
                                    @keyup="
                                        trimFlashcardContent(
                                            index,
                                            'back_content',
                                            flashcard.back_content
                                        )
                                    "
                                />
                            </div>
                            <div class="absolute left-2 top-2">
                                <form-checkbox
                                    :checked-colors="['#FFFFFF', '#0685BB']"
                                    :unchecked-colors="['#DDE1EA', '#ffffff']"
                                    :px-width="22"
                                    v-model="selectedFlashcards[index]"
                                    :value="index"
                                />
                            </div>
                            <div
                                @click="swapCard(flashcard)"
                                class="absolute left-[calc(50%-16px)] top-[calc(50%-16px)] opacity-75 hover:cursor-pointer hover:opacity-100"
                            >
                                <svg
                                    width="36"
                                    height="36"
                                    viewBox="0 0 36 36"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <rect width="36" height="36" fill="white" />
                                    <rect
                                        x="0.5"
                                        y="0.5"
                                        width="35"
                                        height="35"
                                        rx="3.5"
                                        fill="white"
                                    />
                                    <path
                                        d="M14.25 25.5L10.5 21.75M10.5 21.75L14.25 18M10.5 21.75H21.75M21.75 10.5L25.5 14.25M25.5 14.25L21.75 18M25.5 14.25H14.25"
                                        stroke="#767C8D"
                                        stroke-width="1.25"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                    <rect
                                        x="0.5"
                                        y="0.5"
                                        width="35"
                                        height="35"
                                        rx="3.5"
                                        stroke="#CBCED7"
                                    />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </student-backend-focus-layout>
</template>

<script>
import StudentBackendFocusLayout from "@/_Layouts/NewStudentBackendFocusLayout.vue";
import FormCheckbox from "@/Components/FormCheckbox.vue";
import ContentEditable from "vue-contenteditable";
import Dropdown from "@/Components/Dropdown.vue";

export default {
    name: "StudentFlashcardsCreateEditStack",
    components: {
        Dropdown,
        StudentBackendFocusLayout,
        FormCheckbox,
        ContentEditable,
    },
    props: {
        flashcardStack: {
            type: Object,
            default: {},
        },
    },
    data() {
        return {
            editingFlashcardStack: this.flashcardStack,
            isNewDeck: this.flashcardStack ? false : true,
            stackTitle: this.flashcardStack
                ? this.flashcardStack.title
                : "Untitled Stack",
            flashcards: this.flashcardStack
                ? this.flashcardStack.flashcards
                : [],
            selectedFlashcards: [],
            selectedFlashcardsCount: 0,
            flashcardLengthLimit: 400,
            showCharacterLimitWarningLimit: 50,
        };
    },
    methods: {
        addACard() {
            this.flashcards.push({
                id: null,
                front_content: "",
                back_content: "",
            });
        },
        swapCard(flashcard) {
            let frontContent = flashcard.front_content;
            let backContent = flashcard.back_content;

            flashcard.front_content = backContent;
            flashcard.back_content = frontContent;
        },
        deleteCard(flashcardIndex) {
            this.flashcards.splice(flashcardIndex, 1);
        },
        swapSelectedCards() {
            let vm = this;
            vm.selectedFlashcards.forEach(function (cardIndex) {
                vm.swapCard(vm.flashcards[cardIndex]);
            });
        },
        deleteSelectedCards() {
            let vm = this;
            let flashcards = vm.flashcards;

            flashcards = flashcards.filter(function (value, index) {
                return vm.selectedFlashcards.indexOf(index) == -1;
            });

            vm.flashcards = flashcards;

            vm.clearSelectedCards();
        },
        selectAllCards() {
            let vm = this;
            vm.flashcards.forEach(function (flashcard, index) {
                vm.selectedFlashcards[index] = index;
            });
        },
        clearSelectedCards() {
            this.selectedFlashcards = [];
        },
        trimFlashcardContent(index, side, flashcardContent) {
            this.flashcards[index][side] = flashcardContent.slice(
                0,
                this.flashcardLengthLimit
            );
        },
        saveStack() {
            let vm = this;
            axios
                .post("/student/flashcards/save-stack/", {
                    stack_id: vm.editingFlashcardStack
                        ? vm.editingFlashcardStack.id
                        : null,
                    title: vm.stackTitle,
                    cards: vm.flashcards,
                })
                .then(function (response) {
                    vm.editingFlashcardStack = response.data;
                    vm.isNewDeck = false;
                });
        },
        throttledSaveStack() {
            this.saveStack();
        },
        deleteStack() {
            this.$inertia.get(
                route("student.flashcards.delete-stack", {
                    flashcard_stack: this.flashcardStack.id,
                })
            );
        },
        saveAndExit() {
            this.saveStack();
            this.$inertia.get(route("student.flashcards.index"));
        },
    },
    watch: {
        selectedFlashcards: {
            handler() {
                let selectedFlashcardsCount = 0;

                this.selectedFlashcards.forEach(function (cardIndex) {
                    if (cardIndex !== null) {
                        selectedFlashcardsCount++;
                    }
                });

                this.selectedFlashcardsCount = selectedFlashcardsCount;
            },
            deep: true,
        },
    },
};
</script>

<style scoped></style>
